import { useEffect, useState } from 'react';
import axios from 'axios';
import { useHistory } from 'react-router-dom';
import Container from '../../components/Container';
import Header from '../../components/Header';
import Button from '../../components/Button';
import FlexContainer from '../../components/FlexContainer';

interface RosterEntry {
    id: string;
    guestID: string;
    firstName: string;
    lastName: string;
    stageName: string;
}

const OCCASIONS = [
    'Birthday',
    'Bachelor Party',
    'Bachelorette Party',
    'Divorce',
    'Promotion',
    'Just Because',
    'Other',
];

const SacrificePage = () => {
    const history = useHistory();
    const [rows, setRows] = useState<RosterEntry[] | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [busy, setBusy] = useState(false);
    const [done, setDone] = useState<null | {
        name: string;
        occasion: string;
        age?: number;
        girls: RosterEntry[];
    }>(null);

    const [name, setName] = useState('');
    const [occasion, setOccasion] = useState('Birthday');
    const [age, setAge] = useState<string>('');
    const [selected, setSelected] = useState<Set<string>>(new Set());

    useEffect(() => {
        (async () => {
            try {
                const { data } = await axios.get<RosterEntry[]>(
                    '/api/sso/roster'
                );
                setRows(data);
            } catch (e: any) {
                setError(e?.response?.data?.message || 'Failed to load roster.');
            }
        })();
    }, []);

    const toggle = (id: string) => {
        setSelected((prev) => {
            const next = new Set(prev);
            if (next.has(id)) {
                next.delete(id);
            } else {
                if (next.size >= 4) return prev;
                next.add(id);
            }
            return next;
        });
    };

    const submit = async () => {
        setError(null);
        if (!name.trim()) {
            setError('Enter the name of the guest being sacrificed.');
            return;
        }
        if (selected.size !== 4) {
            setError('Pick exactly 4 girls.');
            return;
        }
        const girls = (rows || []).filter((r) => selected.has(r.id));
        const payload: any = {
            sacrificeeName: name.trim(),
            occasion,
            girls: girls.map((g) => ({
                guestID: g.guestID,
                stageName: g.stageName,
            })),
        };
        if (occasion === 'Birthday' && age.trim()) {
            payload.age = Number(age);
        }
        setBusy(true);
        try {
            await axios.post('/api/sacrifices', payload);
            setDone({
                name: name.trim(),
                occasion,
                age: occasion === 'Birthday' && age.trim() ? Number(age) : undefined,
                girls,
            });
        } catch (e: any) {
            setError(e?.response?.data?.message || 'Submit failed.');
        } finally {
            setBusy(false);
        }
    };

    if (done) {
        return (
            <Container containerName={'landing'}>
                <Header
                    title={'Sacrifice Confirmed'}
                    subtitle={''}
                    bottomBorder={true}
                    padding={true}
                />
                <FlexContainer>
                    <h3 className="transaction-container__text">
                        Sacrificing:{' '}
                        <span className="green-span">{done.name}</span>
                    </h3>
                    <h3 className="transaction-container__text">
                        Occasion:{' '}
                        <span className="green-span">{done.occasion}</span>
                        {done.occasion === 'Birthday' && done.age != null && (
                            <>
                                {' '}— turning{' '}
                                <span className="green-span">{done.age}</span>
                            </>
                        )}
                    </h3>
                    <h3 className="transaction-container__text">
                        <span className="red-span">Selected:</span>
                    </h3>
                    <table className="transaction-container__form--table">
                        <thead>
                            <tr>
                                <th>Stage</th>
                                <th>Name</th>
                            </tr>
                        </thead>
                        <tbody>
                            {done.girls.map((g) => (
                                <tr key={g.id}>
                                    <td className="transaction-container__form--table-stage">
                                        {(g.stageName || '—').toUpperCase()}
                                    </td>
                                    <td className="transaction-container__form--table-value">
                                        {`${g.firstName} ${g.lastName}`.toUpperCase()}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                    <Button
                        color={'green'}
                        margin={true}
                        onClick={() => history.push('/kiosk')}
                    >
                        Done
                    </Button>
                </FlexContainer>
            </Container>
        );
    }

    return (
        <Container containerName={'landing'}>
            <Header
                title={'Sacrifice'}
                subtitle={'Pick 4'}
                bottomBorder={true}
                padding={true}
            />
            <FlexContainer>
                {error && (
                    <h3 className="transaction-container__text">
                        <span className="red-span">{error}</span>
                    </h3>
                )}

                <span className="form-span">Who is being sacrificed?</span>
                <input
                    className="form-input"
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Guest name"
                />

                <span className="form-span">What's the occasion?</span>
                <select
                    className="select-input"
                    value={occasion}
                    onChange={(e) => setOccasion(e.target.value)}
                >
                    {OCCASIONS.map((o) => (
                        <option key={o} value={o}>
                            {o}
                        </option>
                    ))}
                </select>

                {occasion === 'Birthday' && (
                    <>
                        <span className="form-span">How old?</span>
                        <input
                            className="form-input"
                            type="number"
                            min={0}
                            value={age}
                            onChange={(e) => setAge(e.target.value)}
                            placeholder="Age"
                        />
                    </>
                )}

                <h3 className="transaction-container__text">
                    Pick 4 from Who's Here{' '}
                    <span className="green-span">({selected.size}/4)</span>
                </h3>

                {!rows && <h3 className="transaction-container__text">Loading…</h3>}

                {rows && rows.length === 0 && (
                    <h3 className="transaction-container__text">
                        No one is checked in yet today.
                    </h3>
                )}

                {rows && rows.length > 0 && (
                    <table className="transaction-container__form--table">
                        <thead>
                            <tr>
                                <th>Pick</th>
                                <th>Stage</th>
                                <th>Name</th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map((r) => {
                                const isSelected = selected.has(r.id);
                                return (
                                    <tr
                                        id={r.id}
                                        key={r.id}
                                        onClick={() => toggle(r.id)}
                                        style={{
                                            cursor: 'pointer',
                                            background: isSelected
                                                ? '#d8f5d8'
                                                : undefined,
                                        }}
                                    >
                                        <td className="transaction-container__form--table-value">
                                            <input
                                                type="checkbox"
                                                checked={isSelected}
                                                onChange={() => toggle(r.id)}
                                                disabled={
                                                    !isSelected &&
                                                    selected.size >= 4
                                                }
                                            />
                                        </td>
                                        <td className="transaction-container__form--table-stage">
                                            {(r.stageName || '—').toUpperCase()}
                                        </td>
                                        <td className="transaction-container__form--table-value">
                                            {`${r.firstName} ${r.lastName}`.toUpperCase()}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                )}

                <Button color={'green'} margin={true} onClick={submit}>
                    {busy ? 'Saving…' : 'Confirm Sacrifice'}
                </Button>
                <Button
                    color={'red'}
                    margin={true}
                    onClick={() => history.push('/kiosk')}
                >
                    Cancel
                </Button>
            </FlexContainer>
        </Container>
    );
};

export default SacrificePage;
